import React, { useState } from 'react';
import ReactDom from 'react-dom';
import { Button } from 'lib/components';
import classes from './index.module.scss';
import { TextField } from 'lib/components/FormFields';

/**
 * Props interface for the VoidInvoiceItemsModal component
 */
interface Props {
	/** Controls whether the modal is visible */
	isOpen: boolean;
	/** Callback function when modal is closed */
	onClose: () => any;
	/** Callback function when void items action is confirmed with reason */
	onVoidInvoiceItems: (reason: string) => void;
}

/**
 * VoidInvoiceItemsModal component
 * Modal dialog for entering a reason when voiding specific invoice items
 * Renders a text area for entering the void reason and action buttons
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element | null} - Rendered component or null if not open
 */
const VoidInvoiceItemsModal = ({ isOpen, onClose, onVoidInvoiceItems }: Props) => {
	// State for the void reason text input
	const [reason, setReason] = useState('');

	// Don't render anything if modal is not open
	if (!isOpen) {
		return null;
	}

	// Use React Portal to render the modal outside the normal DOM hierarchy
	return ReactDom.createPortal(
		<div className={classes['modal']}>
			<div className={classes['modal-content']}>
				<div className={classes['modal-items']}>
					{/* Form with text area for entering void reason */}
					<div className={classes['modal-form']}>
						<TextField
							placeholder="Enter your void reasoning"
							label="Void Reasoning (required)"
							variant="labelled"
							textArea
							rows={9}
							value={reason}
							onChange={(e) => setReason(e.target.value)}
						/>
					</div>
					{/* Footer with action buttons */}
					<div className={classes['modal-footer']}>
						{/* Confirm button */}
						<Button
							variant="standard"
							color="primary"
							title="Void Items"
							style={{
								marginRight: '1rem',
							}}
							onClick={() => {
								onVoidInvoiceItems(reason);
								setReason(''); // Reset the reason field after submission
								onClose();
							}}
						/>
						{/* Cancel button */}
						<Button variant="standard" color="gray" sizing="small" title="Cancel" onClick={onClose} />
					</div>
				</div>
			</div>
		</div>,
		document.getElementById('portal') as HTMLElement
	);
};

export default VoidInvoiceItemsModal;
